import {
  ChatModelAdapter,
  ChatModelRunResult,
  ThreadMessage,
} from "@assistant-ui/react";
import axios from "axios";
import {
  ChatCommandRouterServer,
  MockScenarioId,
  MockStreamSpeed,
  MockScenarioResponse,
  ChatErrorPayload,
  ChatPart,
  SuggestionItem,
} from "../types";
import {
  getScenarioResponse,
  parseDemoScenarioCommandFromInput,
} from "./mockScenarios";
import { streamMockTextChunks } from "./mockStream";

type CreateMockChatModelAdapterOptions = {
  getStreamSpeed: () => MockStreamSpeed;
  getSelectedCustomerId?: () => string | null;
  getAuthorizationToken?: () => string | null;
  refreshAuthorizationToken?: (
    currentToken: string | null
  ) => Promise<string | null>;
  getCommandRouterServer?: () => ChatCommandRouterServer;
  getCommandRouterSessionId?: () => string;
  setCommandRouterSessionId?: (sessionId: string) => void;
  onScenarioResolved?: (scenarioId: MockScenarioId) => void;
  onPrescriptionCommandResolved?: (markdownText: string) => void;
  shouldIgnorePrescriptionCommands?: () => boolean;
};

type RemoteCommandRouteResult =
  | {
      ok: true;
      commandItems: NormalizedCommandItem[];
      hasSessionId: boolean;
      sessionId: string;
    }
  | {
      ok: false;
      errorMessage: ChatErrorPayload;
    };

type ResolvedInputResponse = {
  primaryResponse: ResolvedCommandResponse;
  additionalResponses: ResolvedCommandResponse[];
};

type ResolvedCommandResponse = {
  command: string;
  response: MockScenarioResponse;
};

type AdditionalResponseMetadata = {
  scenarioId: MockScenarioId;
  parts: ChatPart[];
};

type NormalizedCommandItem = {
  command: string;
  data: unknown;
  raw: unknown;
  hasSessionId: boolean;
  sessionId: string;
};

type CommandDataEnvelope = {
  parts: ChatPart[];
  streamText?: string;
  error?: ChatErrorPayload;
};
const REMOTE_COMMAND_ROUTER_PROD_URL =
  process.env.REACT_APP_CHAT_COMMAND_ROUTER_PROD_URL ||
  "https://pnoe-ai-prod.victorioushill-83072029.westus2.azurecontainerapps.io/chat/dashboard_bot";
const REMOTE_COMMAND_ROUTER_DEV_URL =
  process.env.REACT_APP_CHAT_COMMAND_ROUTER_DEV_URL ||
  "https://pnoe-ai-dev.victorioushill-83072029.westus2.azurecontainerapps.io/chat/dashboard_bot";

const SUPPORTED_COMMANDS = ["biometrics", "biomarker", "prescription"] as const;

const SUPPORTED_DEMO_PARAMETERS = [
  "plain",
  "image",
  "link",
  "suggestions",
  "metric",
  "metric-picker",
  "graph",
  "meal",
  "workout",
  "error",
] as const;

const DEMO_PARAMETER_TO_SCENARIO: Record<string, MockScenarioId> = {
  plain: "plain_text",
  image: "image_text",
  link: "plain_text",
  suggestions: "suggestions",
  metric: "custom_metric",
  "metric-picker": "metric_picker",
  graph: "custom_graph",
  meal: "meal_item_details",
  workout: "workout_details",
  error: "error_state",
};

const SCENARIO_TO_DEMO_PARAMETER: Partial<Record<MockScenarioId, string>> = {
  plain_text: "plain",
  image_text: "image",
  suggestions: "suggestions",
  custom_metric: "metric",
  metric_picker: "metric-picker",
  custom_graph: "graph",
  meal_item_details: "meal",
  workout_details: "workout",
  error_state: "error",
};

const resolveCommandRouterUpstreamUrl = (
  commandRouterServer: ChatCommandRouterServer
) => {
  return commandRouterServer === "dev"
    ? REMOTE_COMMAND_ROUTER_DEV_URL
    : REMOTE_COMMAND_ROUTER_PROD_URL;
};

const getLatestUserInput = (messages: readonly ThreadMessage[]): string => {
  for (let i = messages.length - 1; i >= 0; i -= 1) {
    const message = messages[i];
    if (message.role !== "user") {
      continue;
    }

    const textParts = message.content.reduce<string[]>((parts, part) => {
      if (part.type === "text") {
        parts.push(part.text);
      }

      return parts;
    }, []);

    return textParts.join("\n").trim();
  }

  return "";
};

const mapChatPartToAssistantContent = (part: ChatPart) => {
  if (part.type === "text") {
    return {
      type: "text" as const,
      text: part.text,
    };
  }

  if (part.type === "image") {
    return {
      type: "image" as const,
      image: part.src,
      filename: part.alt,
    };
  }

  if (part.type === "link_list") {
    return {
      type: "data" as const,
      name: "link_list",
      data: {
        items: part.items,
      },
    };
  }

  if (part.type === "suggestions") {
    return {
      type: "data" as const,
      name: "suggestions",
      data: {
        items: part.items,
      },
    };
  }

  if (part.type === "custom_trend_chart") {
    return {
      type: "data" as const,
      name: "custom_trend_chart",
      data: part.data,
    };
  }

  if (part.type === "metric_card_suggestions") {
    return {
      type: "data" as const,
      name: "metric_card_suggestions",
      data: part.data,
    };
  }

  if (part.type === "biomarker_card_suggestions") {
    return {
      type: "data" as const,
      name: "biomarker_card_suggestions",
      data: part.data,
    };
  }

  if (part.type === "biomarker_detail") {
    return {
      type: "data" as const,
      name: "biomarker_detail",
      data: part.data,
    };
  }

  if (part.type === "biometrics_recap") {
    return {
      type: "data" as const,
      name: "biometrics_recap",
      data: part.data,
    };
  }

  if (part.type === "navigation_action") {
    return {
      type: "data" as const,
      name: "navigation_action",
      data: part.data,
    };
  }

  switch (part.type) {
    case "custom_metric_card":
      return {
        type: "data" as const,
        name: "custom_metric_card",
        data: part.data,
      };
    case "custom_meal_item_details":
      return {
        type: "data" as const,
        name: "custom_meal_item_details",
        data: part.data,
      };
    case "custom_workout_details":
      return {
        type: "data" as const,
        name: "custom_workout_details",
        data: part.data,
      };
  }
};

const toStaticResponseParts = (response: MockScenarioResponse): ChatPart[] => {
  if (!response.streamText) {
    return response.parts;
  }

  const staticParts = response.parts.filter((part) => part.type !== "text");
  return [
    {
      type: "text",
      text: response.streamText,
    },
    ...staticParts,
  ];
};

const toAdditionalResponseMetadata = (
  response: MockScenarioResponse
): AdditionalResponseMetadata => {
  return {
    scenarioId: response.scenarioId,
    parts: toStaticResponseParts(response),
  };
};

const toRunResult = (
  response: MockScenarioResponse,
  additionalResponses: MockScenarioResponse[] = []
): ChatModelRunResult => {
  return {
    content: toStaticResponseParts(response).map(mapChatPartToAssistantContent),
    metadata: {
      custom: {
        mockScenarioId: response.scenarioId,
        mockAdditionalResponses: additionalResponses.map(
          toAdditionalResponseMetadata
        ),
      },
    },
  };
};

const streamScenario = async function* (
  response: MockScenarioResponse,
  additionalResponses: MockScenarioResponse[],
  speed: MockStreamSpeed,
  abortSignal: AbortSignal
): AsyncGenerator<ChatModelRunResult, void, unknown> {
  if (!response.streamText) {
    yield toRunResult(response);
    return;
  }

  const staticParts = response.parts.filter((part) => part.type !== "text");

  for await (const chunk of streamMockTextChunks(response.streamText, {
    speed,
    seed: response.scenarioId,
  })) {
    if (abortSignal.aborted) {
      return;
    }

    const inProgressParts: ChatPart[] = [
      {
        type: "text",
        text: chunk,
      },
      ...staticParts,
    ];

    yield {
      content: inProgressParts.map(mapChatPartToAssistantContent),
      metadata: {
        custom: {
          mockScenarioId: response.scenarioId,
        },
      },
    };
  }

  yield toRunResult({
    ...response,
    parts: [
      {
        type: "text",
        text: response.streamText,
      },
      ...staticParts,
    ],
  }, additionalResponses);
};

const toSafeToken = (value: unknown): string => {
  return typeof value === "string" ? value.trim() : "";
};

const isRecord = (value: unknown): value is Record<string, unknown> => {
  return typeof value === "object" && value !== null;
};

const normalizeCommand = (value: unknown) => {
  return toSafeToken(value).toLowerCase();
};

const normalizeCommandItem = (item: unknown, itemPath: string) => {
  if (!isRecord(item)) {
    return {
      item: null as NormalizedCommandItem | null,
      errors: [`${itemPath}: expected object.`],
    };
  }

  const command = normalizeCommand(item.command);
  const hasDataField = Object.prototype.hasOwnProperty.call(item, "data");
  const sessionIdResult = extractSessionIdFromItem(item);

  const errors: string[] = [];
  if (!command) {
    errors.push(`${itemPath}.command: missing or empty.`);
  }

  if (!hasDataField) {
    errors.push(`${itemPath}.data: missing field.`);
  }

  return {
    item:
      errors.length === 0
        ? {
            command,
            data: item.data,
            raw: item,
            hasSessionId: sessionIdResult.hasSessionId,
            sessionId: sessionIdResult.sessionId,
          }
        : null,
    errors,
  };
};

const buildCommandItemsFromPayload = (
  payload: unknown
): {
  commandItems: NormalizedCommandItem[] | null;
  errors: string[];
} => {
  const candidateItems = (() => {
    if (Array.isArray(payload)) {
      return payload;
    }

    if (isRecord(payload) && Array.isArray(payload.commands)) {
      return payload.commands;
    }

    return [payload];
  })();

  const errors: string[] = [];
  const commandItems: NormalizedCommandItem[] = [];

  candidateItems.forEach((candidateItem, index) => {
    const normalized = normalizeCommandItem(candidateItem, `payload[${index}]`);
    if (normalized.errors.length > 0 || !normalized.item) {
      errors.push(...normalized.errors);
      return;
    }

    commandItems.push(normalized.item);
  });

  if (commandItems.length === 0 && errors.length === 0) {
    errors.push("payload: no command items were found.");
  }

  if (errors.length > 0) {
    return {
      commandItems: null,
      errors,
    };
  }

  return {
    commandItems: commandItems.length > 0 ? commandItems : null,
    errors,
  };
};

const extractSessionIdFromItem = (item: unknown) => {
  if (!isRecord(item) || !Object.prototype.hasOwnProperty.call(item, "sessionId")) {
    return {
      hasSessionId: false,
      sessionId: "",
    };
  }

  return {
    hasSessionId: true,
    sessionId: toSafeToken(item.sessionId),
  };
};

const extractSessionIdFromRemotePayload = (payload: unknown) => {
  const topLevelSessionId = extractSessionIdFromItem(payload);
  if (topLevelSessionId.hasSessionId) {
    return topLevelSessionId;
  }

  if (Array.isArray(payload)) {
    for (const item of payload) {
      const sessionIdFromItem = extractSessionIdFromItem(item);
      if (sessionIdFromItem.hasSessionId) {
        return sessionIdFromItem;
      }
    }
  }

  if (isRecord(payload) && Array.isArray(payload.commands)) {
    for (const item of payload.commands) {
      const sessionIdFromItem = extractSessionIdFromItem(item);
      if (sessionIdFromItem.hasSessionId) {
        return sessionIdFromItem;
      }
    }
  }

  return {
    hasSessionId: false,
    sessionId: "",
  };
};

const formatCommandRouterError = (
  code: string,
  message: string,
  details?: string
): ChatErrorPayload => {
  return {
    summaryText: `Error code: ${code}\nError message: ${message}`,
    detailsText: details || "",
    retryLabel: "Retry",
  };
};

const toJson = (value: unknown) => {
  try {
    return JSON.stringify(value, null, 2);
  } catch (error) {
    return String(value);
  }
};

const expectRecord = (
  value: unknown,
  path: string,
  errors: string[]
): Record<string, unknown> | null => {
  if (!isRecord(value)) {
    errors.push(`${path}: expected object.`);
    return null;
  }

  return value;
};

const expectArray = (
  value: unknown,
  path: string,
  errors: string[]
): unknown[] | null => {
  if (!Array.isArray(value)) {
    errors.push(`${path}: expected array.`);
    return null;
  }

  return value;
};

const expectString = (
  value: unknown,
  path: string,
  errors: string[]
): string | null => {
  if (typeof value !== "string") {
    errors.push(`${path}: expected string.`);
    return null;
  }

  return value;
};

const expectOptionalString = (
  value: unknown,
  path: string,
  errors: string[]
): string | undefined => {
  if (value === undefined) {
    return undefined;
  }

  if (typeof value !== "string") {
    errors.push(`${path}: expected string when provided.`);
    return undefined;
  }

  return value;
};

const expectNumber = (
  value: unknown,
  path: string,
  errors: string[]
): number | null => {
  if (typeof value !== "number" || Number.isNaN(value)) {
    errors.push(`${path}: expected number.`);
    return null;
  }

  return value;
};

const expectOptionalNumber = (
  value: unknown,
  path: string,
  errors: string[]
): number | undefined => {
  if (value === undefined) {
    return undefined;
  }

  if (typeof value !== "number" || Number.isNaN(value)) {
    errors.push(`${path}: expected number when provided.`);
    return undefined;
  }

  return value;
};

const expectBoolean = (
  value: unknown,
  path: string,
  errors: string[]
): boolean | null => {
  if (typeof value !== "boolean") {
    errors.push(`${path}: expected boolean.`);
    return null;
  }

  return value;
};

const hasKeys = (
  value: Record<string, unknown>,
  path: string,
  keys: string[],
  errors: string[]
) => {
  keys.forEach((key) => {
    if (!Object.prototype.hasOwnProperty.call(value, key)) {
      errors.push(`${path}.${key}: missing field.`);
    }
  });
};

const validateSuggestionItem = (
  value: unknown,
  path: string,
  errors: string[]
): SuggestionItem | null => {
  const record = expectRecord(value, path, errors);
  if (!record) {
    return null;
  }

  hasKeys(record, path, ["id", "label", "prompt", "sendMode"], errors);
  const id = expectString(record.id, `${path}.id`, errors);
  const label = expectString(record.label, `${path}.label`, errors);
  const prompt = expectString(record.prompt, `${path}.prompt`, errors);
  const sendMode = expectString(record.sendMode, `${path}.sendMode`, errors);
  if (sendMode && sendMode !== "immediate") {
    errors.push(`${path}.sendMode: expected "immediate".`);
  }

  if (!id || !label || !prompt || !sendMode || sendMode !== "immediate") {
    return null;
  }

  return {
    id,
    label,
    prompt,
    sendMode: "immediate",
  };
};

const validateChatErrorPayload = (
  value: unknown,
  path: string,
  errors: string[]
): ChatErrorPayload | null => {
  const record = expectRecord(value, path, errors);
  if (!record) {
    return null;
  }

  hasKeys(record, path, ["summaryText", "retryLabel"], errors);
  const summaryText = expectString(record.summaryText, `${path}.summaryText`, errors);
  const detailsText = expectOptionalString(
    record.detailsText,
    `${path}.detailsText`,
    errors
  );
  const retryLabel = expectString(record.retryLabel, `${path}.retryLabel`, errors);
  if (!summaryText || !retryLabel) {
    return null;
  }

  return {
    summaryText,
    detailsText,
    retryLabel,
  };
};

const validateMetricCardPayload = (
  value: unknown,
  path: string,
  errors: string[]
) => {
  const record = expectRecord(value, path, errors);
  if (!record) {
    return null;
  }

  hasKeys(
    record,
    path,
    [
      "metricId",
      "title",
      "score",
      "status",
      "trend",
      "statusLabel",
      "trendLabel",
      "summary",
    ],
    errors
  );
  const metricId = expectString(record.metricId, `${path}.metricId`, errors);
  const title = expectString(record.title, `${path}.title`, errors);
  const score = expectNumber(record.score, `${path}.score`, errors);
  const status = expectString(record.status, `${path}.status`, errors);
  if (status && !["good", "watch", "risk"].includes(status)) {
    errors.push(`${path}.status: expected one of good/watch/risk.`);
  }
  const trend = expectString(record.trend, `${path}.trend`, errors);
  if (trend && !["up", "down", "flat"].includes(trend)) {
    errors.push(`${path}.trend: expected one of up/down/flat.`);
  }
  const statusLabel = expectString(record.statusLabel, `${path}.statusLabel`, errors);
  const trendLabel = expectString(record.trendLabel, `${path}.trendLabel`, errors);
  const summary = expectString(record.summary, `${path}.summary`, errors);
  const deltaLabel = expectOptionalString(
    record.deltaLabel,
    `${path}.deltaLabel`,
    errors
  );

  if (
    !metricId ||
    !title ||
    score === null ||
    !status ||
    !["good", "watch", "risk"].includes(status) ||
    !trend ||
    !["up", "down", "flat"].includes(trend) ||
    !statusLabel ||
    !trendLabel ||
    !summary
  ) {
    return null;
  }

  return {
    metricId,
    title,
    score,
    status,
    trend,
    statusLabel,
    trendLabel,
    summary,
    deltaLabel,
  };
};

const validateMetricCardSuggestionsPayload = (
  value: unknown,
  path: string,
  errors: string[]
) => {
  const record = expectRecord(value, path, errors);
  if (!record) {
    return null;
  }

  hasKeys(record, path, ["items", "ui"], errors);
  const items = expectArray(record.items, `${path}.items`, errors);
  const ui = expectRecord(record.ui, `${path}.ui`, errors);
  if (!items || !ui) {
    return null;
  }

  hasKeys(ui, `${path}.ui`, ["selectedBadgeLabel"], errors);
  const selectedBadgeLabel = expectString(
    ui.selectedBadgeLabel,
    `${path}.ui.selectedBadgeLabel`,
    errors
  );

  const parsedItems = items
    .map((item, index) => {
      const itemRecord = expectRecord(item, `${path}.items[${index}]`, errors);
      if (!itemRecord) {
        return null;
      }

      const metricCard = validateMetricCardPayload(
        itemRecord,
        `${path}.items[${index}]`,
        errors
      );
      hasKeys(itemRecord, `${path}.items[${index}]`, ["id", "prompt", "sendMode"], errors);
      const id = expectString(itemRecord.id, `${path}.items[${index}].id`, errors);
      const prompt = expectString(
        itemRecord.prompt,
        `${path}.items[${index}].prompt`,
        errors
      );
      const sendMode = expectString(
        itemRecord.sendMode,
        `${path}.items[${index}].sendMode`,
        errors
      );
      if (sendMode && sendMode !== "immediate") {
        errors.push(`${path}.items[${index}].sendMode: expected "immediate".`);
      }
      if (!metricCard || !id || !prompt || sendMode !== "immediate") {
        return null;
      }

      return {
        ...metricCard,
        id,
        prompt,
        sendMode: "immediate" as const,
      };
    })
    .filter((item): item is NonNullable<typeof item> => item !== null);

  if (parsedItems.length !== items.length || !selectedBadgeLabel) {
    return null;
  }

  return {
    items: parsedItems,
    ui: {
      selectedBadgeLabel,
    },
  };
};

const validateBiomarkerSuggestionsPayload = (
  value: unknown,
  path: string,
  errors: string[]
) => {
  const record = expectRecord(value, path, errors);
  if (!record) {
    return null;
  }

  hasKeys(record, path, ["metricId", "metricName", "systemSlug", "groups", "ui"], errors);
  const metricId = expectNumber(record.metricId, `${path}.metricId`, errors);
  const metricName = expectString(record.metricName, `${path}.metricName`, errors);
  const systemSlug = expectString(record.systemSlug, `${path}.systemSlug`, errors);
  const groups = expectArray(record.groups, `${path}.groups`, errors);
  const ui = expectRecord(record.ui, `${path}.ui`, errors);
  if (metricId === null || !metricName || !systemSlug || !groups || !ui) {
    return null;
  }

  hasKeys(ui, `${path}.ui`, ["selectedBadgeLabel"], errors);
  const selectedBadgeLabel = expectString(
    ui.selectedBadgeLabel,
    `${path}.ui.selectedBadgeLabel`,
    errors
  );

  const parsedGroups = groups
    .map((group, groupIndex) => {
      const groupRecord = expectRecord(group, `${path}.groups[${groupIndex}]`, errors);
      if (!groupRecord) {
        return null;
      }

      hasKeys(groupRecord, `${path}.groups[${groupIndex}]`, ["testKind", "testKindLabel", "items"], errors);
      const testKind = expectNumber(
        groupRecord.testKind,
        `${path}.groups[${groupIndex}].testKind`,
        errors
      );
      const testKindLabel = expectString(
        groupRecord.testKindLabel,
        `${path}.groups[${groupIndex}].testKindLabel`,
        errors
      );
      const items = expectArray(
        groupRecord.items,
        `${path}.groups[${groupIndex}].items`,
        errors
      );

      if (testKind === null || !testKindLabel || !items) {
        return null;
      }

      const parsedItems = items
        .map((item, itemIndex) => {
          const itemRecord = expectRecord(
            item,
            `${path}.groups[${groupIndex}].items[${itemIndex}]`,
            errors
          );
          if (!itemRecord) {
            return null;
          }

          hasKeys(
            itemRecord,
            `${path}.groups[${groupIndex}].items[${itemIndex}]`,
            [
              "id",
              "biomarkerID",
              "metricId",
              "metricName",
              "systemSlug",
              "name",
              "label",
              "shortName",
              "unit",
              "latestValue",
              "latestValueLabel",
              "latestDateLabel",
              "secondaryLabel",
              "scoreCategory",
              "biomarkerDetail",
              "prompt",
              "sendMode",
            ],
            errors
          );

          const id = expectString(
            itemRecord.id,
            `${path}.groups[${groupIndex}].items[${itemIndex}].id`,
            errors
          );
          const biomarkerID = expectString(
            itemRecord.biomarkerID,
            `${path}.groups[${groupIndex}].items[${itemIndex}].biomarkerID`,
            errors
          );
          const metricIdValue = expectNumber(
            itemRecord.metricId,
            `${path}.groups[${groupIndex}].items[${itemIndex}].metricId`,
            errors
          );
          const metricNameValue = expectString(
            itemRecord.metricName,
            `${path}.groups[${groupIndex}].items[${itemIndex}].metricName`,
            errors
          );
          const systemSlugValue = expectString(
            itemRecord.systemSlug,
            `${path}.groups[${groupIndex}].items[${itemIndex}].systemSlug`,
            errors
          );
          const name = expectString(
            itemRecord.name,
            `${path}.groups[${groupIndex}].items[${itemIndex}].name`,
            errors
          );
          const label =
            itemRecord.label === null
              ? null
              : expectString(
                  itemRecord.label,
                  `${path}.groups[${groupIndex}].items[${itemIndex}].label`,
                  errors
                );
          const shortName =
            itemRecord.shortName === null
              ? null
              : expectString(
                  itemRecord.shortName,
                  `${path}.groups[${groupIndex}].items[${itemIndex}].shortName`,
                  errors
                );
          const unit = expectString(
            itemRecord.unit,
            `${path}.groups[${groupIndex}].items[${itemIndex}].unit`,
            errors
          );
          const latestValue = expectNumber(
            itemRecord.latestValue,
            `${path}.groups[${groupIndex}].items[${itemIndex}].latestValue`,
            errors
          );
          const latestDate = expectOptionalString(
            itemRecord.latestDate,
            `${path}.groups[${groupIndex}].items[${itemIndex}].latestDate`,
            errors
          );
          const latestValueLabel = expectString(
            itemRecord.latestValueLabel,
            `${path}.groups[${groupIndex}].items[${itemIndex}].latestValueLabel`,
            errors
          );
          const latestDateLabel = expectString(
            itemRecord.latestDateLabel,
            `${path}.groups[${groupIndex}].items[${itemIndex}].latestDateLabel`,
            errors
          );
          const secondaryLabel = expectString(
            itemRecord.secondaryLabel,
            `${path}.groups[${groupIndex}].items[${itemIndex}].secondaryLabel`,
            errors
          );
          const scoreCategory = expectNumber(
            itemRecord.scoreCategory,
            `${path}.groups[${groupIndex}].items[${itemIndex}].scoreCategory`,
            errors
          );
          const biomarkerDetail = validateBiomarkerDetailPayload(
            itemRecord.biomarkerDetail,
            `${path}.groups[${groupIndex}].items[${itemIndex}].biomarkerDetail`,
            errors
          );
          const prompt = expectString(
            itemRecord.prompt,
            `${path}.groups[${groupIndex}].items[${itemIndex}].prompt`,
            errors
          );
          const sendMode = expectString(
            itemRecord.sendMode,
            `${path}.groups[${groupIndex}].items[${itemIndex}].sendMode`,
            errors
          );

          if (sendMode && sendMode !== "immediate") {
            errors.push(
              `${path}.groups[${groupIndex}].items[${itemIndex}].sendMode: expected "immediate".`
            );
          }

          if (
            !id ||
            !biomarkerID ||
            metricIdValue === null ||
            !metricNameValue ||
            !systemSlugValue ||
            !name ||
            !unit ||
            latestValue === null ||
            !latestValueLabel ||
            !latestDateLabel ||
            !secondaryLabel ||
            scoreCategory === null ||
            !biomarkerDetail ||
            !prompt ||
            sendMode !== "immediate"
          ) {
            return null;
          }

          return {
            id,
            biomarkerID,
            metricId: metricIdValue,
            metricName: metricNameValue,
            systemSlug: systemSlugValue,
            name,
            label,
            shortName,
            unit,
            latestValue,
            ...(latestDate !== undefined ? { latestDate } : {}),
            latestValueLabel,
            latestDateLabel,
            secondaryLabel,
            scoreCategory,
            biomarkerDetail,
            prompt,
            sendMode: "immediate" as const,
          };
        })
        .filter((item): item is NonNullable<typeof item> => item !== null);

      if (parsedItems.length !== items.length) {
        return null;
      }

      return {
        testKind,
        testKindLabel,
        items: parsedItems,
      };
    })
    .filter((group): group is NonNullable<typeof group> => group !== null);

  if (parsedGroups.length !== groups.length || !selectedBadgeLabel) {
    return null;
  }

  return {
    metricId,
    metricName,
    systemSlug,
    groups: parsedGroups,
    ui: {
      selectedBadgeLabel,
    },
  };
};

const validateBiomarkerDetailPayload = (
  value: unknown,
  path: string,
  errors: string[]
) => {
  const record = expectRecord(value, path, errors);
  if (!record) {
    return null;
  }

  hasKeys(
    record,
    path,
    [
      "biomarkerID",
      "metricId",
      "metricName",
      "systemSlug",
      "name",
      "label",
      "shortName",
      "unit",
      "scoreCategory",
      "latestValueLabel",
      "latestDateLabel",
      "ui",
      "values",
    ],
    errors
  );
  const biomarkerID = expectString(record.biomarkerID, `${path}.biomarkerID`, errors);
  const metricId = expectNumber(record.metricId, `${path}.metricId`, errors);
  const metricName = expectString(record.metricName, `${path}.metricName`, errors);
  const systemSlug = expectString(record.systemSlug, `${path}.systemSlug`, errors);
  const name = expectString(record.name, `${path}.name`, errors);
  const label =
    record.label === null ? null : expectString(record.label, `${path}.label`, errors);
  const shortName =
    record.shortName === null
      ? null
      : expectString(record.shortName, `${path}.shortName`, errors);
  const unit = expectString(record.unit, `${path}.unit`, errors);
  const scoreCategory = expectNumber(record.scoreCategory, `${path}.scoreCategory`, errors);
  const latestValueLabel = expectString(
    record.latestValueLabel,
    `${path}.latestValueLabel`,
    errors
  );
  const latestDateLabel = expectString(
    record.latestDateLabel,
    `${path}.latestDateLabel`,
    errors
  );
  const ui = expectRecord(record.ui, `${path}.ui`, errors);
  const values = expectArray(record.values, `${path}.values`, errors);
  if (
    !biomarkerID ||
    metricId === null ||
    !metricName ||
    !systemSlug ||
    !name ||
    !unit ||
    scoreCategory === null ||
    !latestValueLabel ||
    !latestDateLabel ||
    !ui ||
    !values
  ) {
    return null;
  }

  hasKeys(
    ui,
    `${path}.ui`,
    [
      "historyValueLabel",
      "historyDateLabel",
      "tooltipSeriesLabel",
      "noHistoryLabel",
      "noHistoryEntriesLabel",
    ],
    errors
  );
  const historyValueLabel = expectString(
    ui.historyValueLabel,
    `${path}.ui.historyValueLabel`,
    errors
  );
  const historyDateLabel = expectString(
    ui.historyDateLabel,
    `${path}.ui.historyDateLabel`,
    errors
  );
  const tooltipSeriesLabel = expectString(
    ui.tooltipSeriesLabel,
    `${path}.ui.tooltipSeriesLabel`,
    errors
  );
  const noHistoryLabel = expectString(ui.noHistoryLabel, `${path}.ui.noHistoryLabel`, errors);
  const noHistoryEntriesLabel = expectString(
    ui.noHistoryEntriesLabel,
    `${path}.ui.noHistoryEntriesLabel`,
    errors
  );

  const parsedValues = values
    .map((valueItem, valueIndex) => {
      const valueRecord = expectRecord(valueItem, `${path}.values[${valueIndex}]`, errors);
      if (!valueRecord) {
        return null;
      }

      hasKeys(
        valueRecord,
        `${path}.values[${valueIndex}]`,
        [
          "biomarkerID",
          "dateCreated",
          "id",
          "value",
          "chartLabel",
          "tooltipLabel",
          "dateLabel",
          "valueLabel",
        ],
        errors
      );
      const entryBiomarkerId = expectString(
        valueRecord.biomarkerID,
        `${path}.values[${valueIndex}].biomarkerID`,
        errors
      );
      const dateCreated = expectString(
        valueRecord.dateCreated,
        `${path}.values[${valueIndex}].dateCreated`,
        errors
      );
      const id = expectString(valueRecord.id, `${path}.values[${valueIndex}].id`, errors);
      const numericValue = expectNumber(
        valueRecord.value,
        `${path}.values[${valueIndex}].value`,
        errors
      );
      const chartLabel = expectString(
        valueRecord.chartLabel,
        `${path}.values[${valueIndex}].chartLabel`,
        errors
      );
      const tooltipLabel = expectString(
        valueRecord.tooltipLabel,
        `${path}.values[${valueIndex}].tooltipLabel`,
        errors
      );
      const dateLabel = expectString(
        valueRecord.dateLabel,
        `${path}.values[${valueIndex}].dateLabel`,
        errors
      );
      const valueLabel = expectString(
        valueRecord.valueLabel,
        `${path}.values[${valueIndex}].valueLabel`,
        errors
      );

      if (
        !entryBiomarkerId ||
        !dateCreated ||
        !id ||
        numericValue === null ||
        !chartLabel ||
        !tooltipLabel ||
        !dateLabel ||
        !valueLabel
      ) {
        return null;
      }

      return {
        biomarkerID: entryBiomarkerId,
        dateCreated,
        id,
        value: numericValue,
        chartLabel,
        tooltipLabel,
        dateLabel,
        valueLabel,
      };
    })
    .filter((item): item is NonNullable<typeof item> => item !== null);

  if (
    parsedValues.length !== values.length ||
    !historyValueLabel ||
    !historyDateLabel ||
    !tooltipSeriesLabel ||
    !noHistoryLabel ||
    !noHistoryEntriesLabel
  ) {
    return null;
  }

  return {
    biomarkerID,
    metricId,
    metricName,
    systemSlug,
    name,
    label,
    shortName,
    unit,
    scoreCategory,
    latestValueLabel,
    latestDateLabel,
    ui: {
      historyValueLabel,
      historyDateLabel,
      tooltipSeriesLabel,
      noHistoryLabel,
      noHistoryEntriesLabel,
    },
    values: parsedValues,
  };
};

const validateBiometricsRecapPayload = (
  value: unknown,
  path: string,
  errors: string[]
) => {
  const record = expectRecord(value, path, errors);
  if (!record) {
    return null;
  }

  hasKeys(record, path, ["title", "clientName", "sections"], errors);
  const title = expectString(record.title, `${path}.title`, errors);
  const clientName = expectString(record.clientName, `${path}.clientName`, errors);
  const sections = expectArray(record.sections, `${path}.sections`, errors);
  if (!title || !clientName || !sections) {
    return null;
  }

  const parsedSections = sections
    .map((section, index) => {
      const sectionRecord = expectRecord(section, `${path}.sections[${index}]`, errors);
      if (!sectionRecord) {
        return null;
      }

      hasKeys(sectionRecord, `${path}.sections[${index}]`, ["id", "title", "items"], errors);
      const id = expectString(sectionRecord.id, `${path}.sections[${index}].id`, errors);
      const sectionTitle = expectString(
        sectionRecord.title,
        `${path}.sections[${index}].title`,
        errors
      );
      const items = expectArray(sectionRecord.items, `${path}.sections[${index}].items`, errors);
      if (!id || !sectionTitle || !items) {
        return null;
      }

      const parsedItems = items
        .map((item, itemIndex) => {
          const itemRecord = expectRecord(
            item,
            `${path}.sections[${index}].items[${itemIndex}]`,
            errors
          );
          if (!itemRecord) {
            return null;
          }

          hasKeys(
            itemRecord,
            `${path}.sections[${index}].items[${itemIndex}]`,
            ["label", "value"],
            errors
          );
          const label = expectString(
            itemRecord.label,
            `${path}.sections[${index}].items[${itemIndex}].label`,
            errors
          );
          const value = expectString(
            itemRecord.value,
            `${path}.sections[${index}].items[${itemIndex}].value`,
            errors
          );
          if (!label || !value) {
            return null;
          }

          return { label, value };
        })
        .filter((item): item is NonNullable<typeof item> => item !== null);

      if (parsedItems.length !== items.length) {
        return null;
      }

      return {
        id,
        title: sectionTitle,
        items: parsedItems,
      };
    })
    .filter((section): section is NonNullable<typeof section> => section !== null);

  if (parsedSections.length !== sections.length) {
    return null;
  }

  return {
    title,
    clientName,
    sections: parsedSections,
  };
};

const validateNavigationActionPayload = (
  value: unknown,
  path: string,
  errors: string[]
) => {
  const record = expectRecord(value, path, errors);
  if (!record) {
    return null;
  }

  hasKeys(record, path, ["label", "targetPath"], errors);
  const label = expectString(record.label, `${path}.label`, errors);
  const targetPath = expectString(record.targetPath, `${path}.targetPath`, errors);
  const description = expectOptionalString(record.description, `${path}.description`, errors);
  if (!label || !targetPath) {
    return null;
  }

  return {
    label,
    targetPath,
    ...(description !== undefined ? { description } : {}),
  };
};

const validateTrendChartPayload = (
  value: unknown,
  path: string,
  errors: string[]
) => {
  const record = expectRecord(value, path, errors);
  if (!record) {
    return null;
  }

  hasKeys(
    record,
    path,
    ["chartId", "title", "metricLabel", "unit", "trend", "trendLabel", "summary", "ui", "points"],
    errors
  );
  const chartId = expectString(record.chartId, `${path}.chartId`, errors);
  const title = expectString(record.title, `${path}.title`, errors);
  const metricLabel = expectString(record.metricLabel, `${path}.metricLabel`, errors);
  const unit = expectString(record.unit, `${path}.unit`, errors);
  const trend = expectString(record.trend, `${path}.trend`, errors);
  if (trend && !["up", "down", "flat"].includes(trend)) {
    errors.push(`${path}.trend: expected one of up/down/flat.`);
  }
  const trendLabel = expectString(record.trendLabel, `${path}.trendLabel`, errors);
  const latestValueLabel = expectOptionalString(
    record.latestValueLabel,
    `${path}.latestValueLabel`,
    errors
  );
  const summary = expectString(record.summary, `${path}.summary`, errors);
  const target = expectOptionalNumber(record.target, `${path}.target`, errors);
  const ui = expectRecord(record.ui, `${path}.ui`, errors);
  const points = expectArray(record.points, `${path}.points`, errors);

  if (
    !chartId ||
    !title ||
    !metricLabel ||
    !unit ||
    !trend ||
    !["up", "down", "flat"].includes(trend) ||
    !trendLabel ||
    !summary ||
    !ui ||
    !points
  ) {
    return null;
  }

  hasKeys(ui, `${path}.ui`, ["targetLabel"], errors);
  const targetLabel = expectString(ui.targetLabel, `${path}.ui.targetLabel`, errors);

  const parsedPoints = points
    .map((point, index) => {
      const pointRecord = expectRecord(point, `${path}.points[${index}]`, errors);
      if (!pointRecord) {
        return null;
      }

      hasKeys(pointRecord, `${path}.points[${index}]`, ["label", "value"], errors);
      const label = expectString(pointRecord.label, `${path}.points[${index}].label`, errors);
      const value = expectNumber(pointRecord.value, `${path}.points[${index}].value`, errors);
      if (!label || value === null) {
        return null;
      }

      return { label, value };
    })
    .filter((point): point is NonNullable<typeof point> => point !== null);

  if (parsedPoints.length !== points.length || !targetLabel) {
    return null;
  }

  return {
    chartId,
    title,
    metricLabel,
    unit,
    trend,
    trendLabel,
    ...(latestValueLabel !== undefined ? { latestValueLabel } : {}),
    summary,
    ...(target !== undefined ? { target } : {}),
    ui: {
      targetLabel,
    },
    points: parsedPoints,
  };
};

const validateMealItemDetailsPayload = (
  value: unknown,
  path: string,
  errors: string[]
) => {
  const record = expectRecord(value, path, errors);
  if (!record) {
    return null;
  }

  hasKeys(
    record,
    path,
    ["id", "kind", "title", "imageUrl", "statusTags", "metadata", "nutritionFacts", "ui"],
    errors
  );
  const id = expectString(record.id, `${path}.id`, errors);
  const kind = expectString(record.kind, `${path}.kind`, errors);
  if (kind && !["recipe", "food"].includes(kind)) {
    errors.push(`${path}.kind: expected recipe/food.`);
  }
  const title = expectString(record.title, `${path}.title`, errors);
  const subtitle = expectOptionalString(record.subtitle, `${path}.subtitle`, errors);
  const imageUrl = expectString(record.imageUrl, `${path}.imageUrl`, errors);
  const fallbackImageUrl = expectOptionalString(
    record.fallbackImageUrl,
    `${path}.fallbackImageUrl`,
    errors
  );
  const statusTags = expectArray(record.statusTags, `${path}.statusTags`, errors);
  const metadata = expectArray(record.metadata, `${path}.metadata`, errors);
  const nutritionFacts = expectRecord(record.nutritionFacts, `${path}.nutritionFacts`, errors);
  const ui = expectRecord(record.ui, `${path}.ui`, errors);

  if (
    !id ||
    !kind ||
    !["recipe", "food"].includes(kind) ||
    !title ||
    !imageUrl ||
    !statusTags ||
    !metadata ||
    !nutritionFacts ||
    !ui
  ) {
    return null;
  }

  return {
    ...record,
    id,
    kind,
    title,
    ...(subtitle !== undefined ? { subtitle } : {}),
    imageUrl,
    ...(fallbackImageUrl !== undefined ? { fallbackImageUrl } : {}),
  };
};

const validateWorkoutDetailsPayload = (
  value: unknown,
  path: string,
  errors: string[]
) => {
  const record = expectRecord(value, path, errors);
  if (!record) {
    return null;
  }

  hasKeys(record, path, ["id", "title", "dayLabel", "activityType", "isScheduled", "summaryMetrics", "ui"], errors);
  const id = expectString(record.id, `${path}.id`, errors);
  const title = expectString(record.title, `${path}.title`, errors);
  const dayLabel = expectString(record.dayLabel, `${path}.dayLabel`, errors);
  const activityType = expectString(record.activityType, `${path}.activityType`, errors);
  const isScheduled = expectBoolean(record.isScheduled, `${path}.isScheduled`, errors);
  const summaryMetrics = expectArray(record.summaryMetrics, `${path}.summaryMetrics`, errors);
  const ui = expectRecord(record.ui, `${path}.ui`, errors);
  if (!id || !title || !dayLabel || !activityType || isScheduled === null || !summaryMetrics || !ui) {
    return null;
  }

  return {
    ...record,
    id,
    title,
    dayLabel,
    activityType,
    isScheduled,
  };
};

const validateChatPart = (
  value: unknown,
  path: string,
  errors: string[]
): ChatPart | null => {
  const record = expectRecord(value, path, errors);
  if (!record) {
    return null;
  }

  hasKeys(record, path, ["type"], errors);
  const partType = expectString(record.type, `${path}.type`, errors);
  if (!partType) {
    return null;
  }

  switch (partType) {
    case "text": {
      hasKeys(record, path, ["text"], errors);
      const text = expectString(record.text, `${path}.text`, errors);
      const markdownValue = record.markdown;
      if (markdownValue !== undefined && typeof markdownValue !== "boolean") {
        errors.push(`${path}.markdown: expected boolean when provided.`);
      }
      if (!text) {
        return null;
      }

      return {
        type: "text",
        text,
        ...(typeof markdownValue === "boolean" ? { markdown: markdownValue } : {}),
      };
    }
    case "image": {
      hasKeys(record, path, ["src", "alt"], errors);
      const src = expectString(record.src, `${path}.src`, errors);
      const alt = expectString(record.alt, `${path}.alt`, errors);
      const caption = expectOptionalString(record.caption, `${path}.caption`, errors);
      if (!src || !alt) {
        return null;
      }

      return {
        type: "image",
        src,
        alt,
        ...(caption !== undefined ? { caption } : {}),
      };
    }
    case "link_list": {
      hasKeys(record, path, ["items"], errors);
      const items = expectArray(record.items, `${path}.items`, errors);
      if (!items) {
        return null;
      }

      const parsedItems = items
        .map((item, index) => {
          const itemRecord = expectRecord(item, `${path}.items[${index}]`, errors);
          if (!itemRecord) {
            return null;
          }

          hasKeys(itemRecord, `${path}.items[${index}]`, ["href", "label"], errors);
          const href = expectString(itemRecord.href, `${path}.items[${index}].href`, errors);
          const label = expectString(
            itemRecord.label,
            `${path}.items[${index}].label`,
            errors
          );
          const description = expectOptionalString(
            itemRecord.description,
            `${path}.items[${index}].description`,
            errors
          );
          const imageSrc = expectOptionalString(
            itemRecord.imageSrc,
            `${path}.items[${index}].imageSrc`,
            errors
          );
          const imageAlt = expectOptionalString(
            itemRecord.imageAlt,
            `${path}.items[${index}].imageAlt`,
            errors
          );
          const showImageValue = itemRecord.showImage;
          if (showImageValue !== undefined && typeof showImageValue !== "boolean") {
            errors.push(`${path}.items[${index}].showImage: expected boolean when provided.`);
          }
          if (!href || !label) {
            return null;
          }

          return {
            href,
            label,
            ...(description !== undefined ? { description } : {}),
            ...(imageSrc !== undefined ? { imageSrc } : {}),
            ...(imageAlt !== undefined ? { imageAlt } : {}),
            ...(typeof showImageValue === "boolean"
              ? { showImage: showImageValue }
              : {}),
          };
        })
        .filter((item): item is NonNullable<typeof item> => item !== null);

      if (parsedItems.length !== items.length) {
        return null;
      }

      return {
        type: "link_list",
        items: parsedItems,
      };
    }
    case "link": {
      hasKeys(record, path, ["href", "label"], errors);
      const href = expectString(record.href, `${path}.href`, errors);
      const label = expectString(record.label, `${path}.label`, errors);
      const description = expectOptionalString(record.description, `${path}.description`, errors);
      const imageSrc = expectOptionalString(record.imageSrc, `${path}.imageSrc`, errors);
      const imageAlt = expectOptionalString(record.imageAlt, `${path}.imageAlt`, errors);
      const showImageValue = record.showImage;
      if (showImageValue !== undefined && typeof showImageValue !== "boolean") {
        errors.push(`${path}.showImage: expected boolean when provided.`);
      }

      if (!href || !label) {
        return null;
      }

      return {
        type: "link_list",
        items: [
          {
            href,
            label,
            ...(description !== undefined ? { description } : {}),
            ...(imageSrc !== undefined ? { imageSrc } : {}),
            ...(imageAlt !== undefined ? { imageAlt } : {}),
            ...(typeof showImageValue === "boolean"
              ? { showImage: showImageValue }
              : {}),
          },
        ],
      };
    }
    case "suggestions": {
      hasKeys(record, path, ["items"], errors);
      const items = expectArray(record.items, `${path}.items`, errors);
      if (!items) {
        return null;
      }

      const parsedItems = items
        .map((item, index) =>
          validateSuggestionItem(item, `${path}.items[${index}]`, errors)
        )
        .filter((item): item is SuggestionItem => item !== null);

      if (parsedItems.length !== items.length) {
        return null;
      }

      return {
        type: "suggestions",
        items: parsedItems,
      };
    }
    case "custom_metric_card": {
      hasKeys(record, path, ["data"], errors);
      const data = validateMetricCardPayload(record.data, `${path}.data`, errors);
      if (!data) {
        return null;
      }

      return {
        type: "custom_metric_card",
        data,
      } as ChatPart;
    }
    case "metric_card_suggestions": {
      hasKeys(record, path, ["data"], errors);
      const data = validateMetricCardSuggestionsPayload(
        record.data,
        `${path}.data`,
        errors
      );
      if (!data) {
        return null;
      }

      return {
        type: "metric_card_suggestions",
        data,
      } as ChatPart;
    }
    case "biomarker_card_suggestions": {
      hasKeys(record, path, ["data"], errors);
      const data = validateBiomarkerSuggestionsPayload(
        record.data,
        `${path}.data`,
        errors
      );
      if (!data) {
        return null;
      }

      return {
        type: "biomarker_card_suggestions",
        data,
      } as ChatPart;
    }
    case "biomarker_detail": {
      hasKeys(record, path, ["data"], errors);
      const data = validateBiomarkerDetailPayload(record.data, `${path}.data`, errors);
      if (!data) {
        return null;
      }

      return {
        type: "biomarker_detail",
        data,
      } as ChatPart;
    }
    case "biometrics_recap": {
      hasKeys(record, path, ["data"], errors);
      const data = validateBiometricsRecapPayload(record.data, `${path}.data`, errors);
      if (!data) {
        return null;
      }

      return {
        type: "biometrics_recap",
        data,
      } as ChatPart;
    }
    case "navigation_action": {
      hasKeys(record, path, ["data"], errors);
      const data = validateNavigationActionPayload(record.data, `${path}.data`, errors);
      if (!data) {
        return null;
      }

      return {
        type: "navigation_action",
        data,
      } as ChatPart;
    }
    case "custom_trend_chart": {
      hasKeys(record, path, ["data"], errors);
      const data = validateTrendChartPayload(record.data, `${path}.data`, errors);
      if (!data) {
        return null;
      }

      return {
        type: "custom_trend_chart",
        data,
      } as ChatPart;
    }
    case "custom_meal_item_details": {
      hasKeys(record, path, ["data"], errors);
      const data = validateMealItemDetailsPayload(record.data, `${path}.data`, errors);
      if (!data) {
        return null;
      }

      return {
        type: "custom_meal_item_details",
        data,
      } as ChatPart;
    }
    case "custom_workout_details": {
      hasKeys(record, path, ["data"], errors);
      const data = validateWorkoutDetailsPayload(record.data, `${path}.data`, errors);
      if (!data) {
        return null;
      }

      return {
        type: "custom_workout_details",
        data,
      } as ChatPart;
    }
    default:
      errors.push(`${path}.type: unsupported part type "${partType}".`);
      return null;
  }
};

const validateCommandDataEnvelope = (
  data: unknown,
  path: string,
  errors: string[]
): CommandDataEnvelope | null => {
  const record = expectRecord(data, path, errors);
  if (!record) {
    return null;
  }

  hasKeys(record, path, ["parts"], errors);
  const partsValue = expectArray(record.parts, `${path}.parts`, errors);
  if (!partsValue) {
    return null;
  }

  const parts = partsValue
    .map((part, index) => validateChatPart(part, `${path}.parts[${index}]`, errors))
    .filter((part): part is ChatPart => part !== null);

  if (parts.length !== partsValue.length) {
    return null;
  }

  const streamText = expectOptionalString(record.streamText, `${path}.streamText`, errors);

  let errorPayload: ChatErrorPayload | undefined;
  if (record.error !== undefined) {
    const parsedError = validateChatErrorPayload(record.error, `${path}.error`, errors);
    if (!parsedError) {
      return null;
    }
    errorPayload = parsedError;
  }

  return {
    parts,
    ...(streamText !== undefined ? { streamText } : {}),
    ...(errorPayload ? { error: errorPayload } : {}),
  };
};

const inferScenarioId = (
  command: string,
  response: CommandDataEnvelope
): MockScenarioId => {
  if (response.error) {
    return "error_state";
  }

  if (isSupportedDemoParameter(command)) {
    return DEMO_PARAMETER_TO_SCENARIO[command] || "plain_text";
  }

  if (command === "biometrics") {
    return response.parts.some((part) => part.type === "biometrics_recap")
      ? "biometrics_recap"
      : "biometrics_guidance";
  }

  if (command === "biomarker") {
    if (response.parts.some((part) => part.type === "biomarker_detail")) {
      return "biomarker_detail";
    }

    if (response.parts.some((part) => part.type === "biomarker_card_suggestions")) {
      return "biomarker_system_results";
    }

    return "biomarker_guidance";
  }

  return "plain_text";
};

const toMockScenarioResponse = (
  command: string,
  response: CommandDataEnvelope
): MockScenarioResponse => {
  return {
    scenarioId: inferScenarioId(command, response),
    parts: response.parts,
    ...(response.streamText !== undefined ? { streamText: response.streamText } : {}),
    ...(response.error ? { error: response.error } : {}),
  };
};

const isSupportedCommand = (value: string) => {
  return SUPPORTED_COMMANDS.includes(value as (typeof SUPPORTED_COMMANDS)[number]);
};

const isSupportedDemoParameter = (value: string) => {
  return SUPPORTED_DEMO_PARAMETERS.includes(
    value as (typeof SUPPORTED_DEMO_PARAMETERS)[number]
  );
};

const resolveCommandItemToResponse = (
  commandItem: NormalizedCommandItem
): MockScenarioResponse => {
  if (isSupportedDemoParameter(commandItem.command)) {
    if (commandItem.command === "plain" && typeof commandItem.data === "string") {
      return {
        scenarioId: "plain_text",
        parts: [
          {
            type: "text",
            text: commandItem.data,
          },
        ],
      };
    }

    const validationErrors: string[] = [];
    const envelope = validateCommandDataEnvelope(
      commandItem.data,
      "data",
      validationErrors
    );
    if (!envelope) {
      return {
        scenarioId: "error_state",
        parts: [],
        error: formatCommandRouterError(
          "INVALID_PAYLOAD",
          `Invalid payload for "${commandItem.command}".`,
          [
            ...validationErrors,
            "",
            "Raw payload:",
            toJson(commandItem.raw),
          ].join("\n")
        ),
      };
    }

    return toMockScenarioResponse(commandItem.command, envelope);
  }

  if (commandItem.command === "prescription") {
    if (typeof commandItem.data === "string") {
      return {
        scenarioId: "plain_text",
        parts: [
          {
            type: "text",
            text: commandItem.data,
          },
        ],
      };
    }

    const validationErrors: string[] = [];
    const envelope = validateCommandDataEnvelope(
      commandItem.data,
      "data",
      validationErrors
    );
    if (!envelope) {
      return {
        scenarioId: "error_state",
        parts: [],
        error: formatCommandRouterError(
          "INVALID_PAYLOAD",
          `Invalid payload for "${commandItem.command}".`,
          [
            ...validationErrors,
            "",
            "Raw payload:",
            toJson(commandItem.raw),
          ].join("\n")
        ),
      };
    }

    return toMockScenarioResponse(commandItem.command, envelope);
  }

  if (commandItem.command === "demo") {
    return {
      scenarioId: "error_state",
      parts: [],
      error: formatCommandRouterError(
        "INVALID_COMMAND",
        'Unsupported command "demo". Use direct command names such as "plain".',
        toJson(commandItem.raw)
      ),
    };
  }

  if (!isSupportedCommand(commandItem.command)) {
    return {
      scenarioId: "error_state",
      parts: [],
      error: formatCommandRouterError(
        "UNSUPPORTED_COMMAND",
        `Unsupported command "${commandItem.command}".`,
        toJson(commandItem.raw)
      ),
    };
  }

  const validationErrors: string[] = [];
  const envelope = validateCommandDataEnvelope(
    commandItem.data,
    "data",
    validationErrors
  );
  if (!envelope) {
    return {
      scenarioId: "error_state",
      parts: [],
      error: formatCommandRouterError(
        "INVALID_PAYLOAD",
        `Invalid payload for "${commandItem.command}".`,
        [
          ...validationErrors,
          "",
          "Raw payload:",
          toJson(commandItem.raw),
        ].join("\n")
      ),
    };
  }

  return toMockScenarioResponse(commandItem.command, envelope);
};

const toServerText = (payload: unknown): string => {
  if (typeof payload === "string") {
    return payload;
  }

  if (payload === null || payload === undefined) {
    return "";
  }

  try {
    return JSON.stringify(payload);
  } catch (error) {
    return String(payload);
  }
};

const parseServerErrorBody = (payload: unknown) => {
  const rawText = toServerText(payload);
  const trimmedText = rawText.trim();
  if (!trimmedText) {
    return {
      message: "",
      details: "",
    };
  }

  try {
    const parsed = JSON.parse(trimmedText) as {
      detail?: unknown;
      message?: unknown;
      error?: unknown;
    };

    return {
      message:
        toSafeToken(parsed.detail) ||
        toSafeToken(parsed.message) ||
        toSafeToken(parsed.error) ||
        trimmedText,
      details: JSON.stringify(parsed, null, 2),
    };
  } catch (error) {
    return {
      message: trimmedText,
      details: trimmedText,
    };
  }
};

const getServerErrorDetails = (
  status: number,
  statusText: string,
  payload: unknown
) => {
  const parsedBody = parseServerErrorBody(payload);
  const statusLine = `HTTP ${status} ${statusText || ""}`.trim();

  return {
    message:
      parsedBody.message ||
      `Command router request failed with status ${status}.`,
    details: `${statusLine}\n${parsedBody.details || "(empty response body)"}`,
  };
};

const resolveInputFromRemoteCommandApi = async (
  input: string,
  customerId: string | null,
  authorizationToken: string | null,
  refreshAuthorizationToken:
    | ((currentToken: string | null) => Promise<string | null>)
    | undefined,
  commandRouterServer: ChatCommandRouterServer,
  sessionId: string,
  abortSignal: AbortSignal
): Promise<RemoteCommandRouteResult> => {
  const trimmedInput = input.trim();
  if (!trimmedInput) {
    return {
      ok: false,
      errorMessage: formatCommandRouterError(
        "EMPTY_INPUT",
        "Cannot send an empty message to the command router."
      ),
    };
  }

  const requestBody = {
    user_text: trimmedInput,
    customer_id: customerId || "",
    sessionId: sessionId || "",
  };

  try {
    const upstreamUrl = resolveCommandRouterUpstreamUrl(commandRouterServer);
    const postToCommandRouter = async (requestToken: string) =>
      axios.post(upstreamUrl, requestBody, {
        headers: {
          "Content-Type": "application/json",
          ...(requestToken
            ? {
                Authorization: `Bearer ${requestToken}`,
              }
            : {}),
        },
        signal: abortSignal,
        validateStatus: () => true,
      });
    let requestToken = authorizationToken?.trim() || "";
    let response = await postToCommandRouter(requestToken);

    if (response.status === 401 && refreshAuthorizationToken) {
      try {
        const refreshedToken = (
          (await refreshAuthorizationToken(requestToken || null)) || ""
        ).trim();
        if (refreshedToken) {
          requestToken = refreshedToken;
          response = await postToCommandRouter(requestToken);
        }
      } catch (refreshError) {
        // Keep the original 401 handling path below when refresh fails.
      }
    }

    if (response.status < 200 || response.status >= 300) {
      const serverError = getServerErrorDetails(
        response.status,
        response.statusText,
        response.data
      );
      return {
        ok: false,
        errorMessage: formatCommandRouterError(
          `HTTP_${response.status}`,
          serverError.message,
          serverError.details
        ),
      };
    }

    const payload = response.data as unknown;
    const commandItemResult = buildCommandItemsFromPayload(payload);
    const sessionIdResult = extractSessionIdFromRemotePayload(payload);
    if (!commandItemResult.commandItems) {
      return {
        ok: false,
        errorMessage: formatCommandRouterError(
          "INVALID_RESPONSE",
          "Command router response is invalid. Expected command/data payload.",
          [
            ...commandItemResult.errors,
            "",
            "Raw payload:",
            toJson(payload),
          ].join("\n")
        ),
      };
    }

    return {
      ok: true,
      commandItems: commandItemResult.commandItems,
      hasSessionId: sessionIdResult.hasSessionId,
      sessionId: sessionIdResult.sessionId,
    };
  } catch (error) {
    if (
      typeof error === "object" &&
      error !== null &&
      "name" in error &&
      ((error as { name?: string }).name === "AbortError" ||
        (error as { name?: string }).name === "CanceledError")
    ) {
      throw error;
    }

    return {
      ok: false,
      errorMessage: formatCommandRouterError(
        "NETWORK_ERROR",
        error instanceof Error ? error.message : "Command router request failed.",
        error instanceof Error
          ? [
              `upstreamUrl: ${resolveCommandRouterUpstreamUrl(
                commandRouterServer
              )}`,
              `name: ${error.name}`,
              `message: ${error.message}`,
              error.stack ? `stack:\n${error.stack}` : "",
            ]
              .filter(Boolean)
              .join("\n")
          : String(error)
      ),
    };
  }
};

const toEnvelopeFromScenarioResponse = (
  response: MockScenarioResponse
): CommandDataEnvelope => {
  return {
    parts: response.parts,
    ...(response.streamText !== undefined ? { streamText: response.streamText } : {}),
    ...(response.error ? { error: response.error } : {}),
  };
};

const buildLocalDemoCommandItems = (
  input: string
): NormalizedCommandItem[] | null => {
  const parsedScenario = parseDemoScenarioCommandFromInput(input);
  if (!parsedScenario) {
    return null;
  }

  const demoParameter = SCENARIO_TO_DEMO_PARAMETER[parsedScenario.scenarioId];
  if (!demoParameter) {
    return null;
  }

  const scenarioResponse = getScenarioResponse(parsedScenario.scenarioId, {
    plainTextInput: parsedScenario.plainTextInput,
  });

  return [
    {
      command: demoParameter,
      data: toEnvelopeFromScenarioResponse(scenarioResponse),
      raw: {
        source: "local-demo",
        input,
      },
      hasSessionId: false,
      sessionId: "",
    },
  ];
};

const resolveResponsesFromCommandItems = (
  commandItems: NormalizedCommandItem[],
  ignorePrescriptionCommands = false
): ResolvedInputResponse => {
  const responses: ResolvedCommandResponse[] = [];
  const hadPrescriptionCommands = commandItems.some(
    (commandItem) => commandItem.command === "prescription"
  );

  for (const commandItem of commandItems) {
    if (ignorePrescriptionCommands && commandItem.command === "prescription") {
      continue;
    }

    const resolvedResponse = resolveCommandItemToResponse(commandItem);

    if (resolvedResponse.error) {
      return {
        primaryResponse: {
          command: commandItem.command,
          response: resolvedResponse,
        },
        additionalResponses: [],
      };
    }

    responses.push({
      command: commandItem.command,
      response: resolvedResponse,
    });
  }

  if (responses.length === 0) {
    if (ignorePrescriptionCommands && hadPrescriptionCommands) {
      return {
        primaryResponse: {
          command: "",
          response: {
            scenarioId: "plain_text",
            parts: [],
          },
        },
        additionalResponses: [],
      };
    }

    return {
      primaryResponse: {
        command: "",
        response: {
          scenarioId: "error_state",
          parts: [],
          error: formatCommandRouterError(
            "INVALID_RESPONSE",
            "Command router response did not include executable commands."
          ),
        },
      },
      additionalResponses: [],
    };
  }

  return {
    primaryResponse: responses[0],
    additionalResponses: responses.slice(1),
  };
};

const extractMarkdownTextFromResponse = (
  response: MockScenarioResponse
): string => {
  const textParts = response.parts
    .filter((part): part is Extract<ChatPart, { type: "text" }> => {
      return part.type === "text";
    })
    .map((part) => part.text.trim())
    .filter(Boolean);

  if (textParts.length === 0) {
    return "";
  }

  return textParts.join("\n\n");
};

const resolveResponseForInput = async (
  input: string,
  options: CreateMockChatModelAdapterOptions,
  abortSignal: AbortSignal
): Promise<ResolvedInputResponse> => {
  const ignorePrescriptionCommands =
    options.shouldIgnorePrescriptionCommands?.() || false;
  const localDemoCommandItems = buildLocalDemoCommandItems(input);
  if (localDemoCommandItems) {
    return resolveResponsesFromCommandItems(
      localDemoCommandItems,
      ignorePrescriptionCommands
    );
  }

  const routeResult = await resolveInputFromRemoteCommandApi(
    input,
    options.getSelectedCustomerId?.() || null,
    options.getAuthorizationToken?.() || null,
    options.refreshAuthorizationToken,
    options.getCommandRouterServer?.() || "prod",
    options.getCommandRouterSessionId?.() || "",
    abortSignal
  );
  if (!routeResult.ok) {
    return {
      primaryResponse: {
        command: "",
        response: {
          scenarioId: "error_state",
          parts: [],
          error: routeResult.errorMessage,
        },
      },
      additionalResponses: [],
    };
  }

  if (routeResult.hasSessionId) {
    options.setCommandRouterSessionId?.(routeResult.sessionId);
  }

  return resolveResponsesFromCommandItems(
    routeResult.commandItems,
    ignorePrescriptionCommands
  );
};

export const __mockEngineInternals = {
  buildCommandItemsFromPayload,
  resolveCommandItemToResponse,
  buildLocalDemoCommandItems,
};

export const mapAdditionalResponsesFromMessageMetadata = (
  value: unknown
): MockScenarioResponse[] => {
  if (!Array.isArray(value)) {
    return [];
  }

  return value.reduce<MockScenarioResponse[]>((responses, item) => {
    if (!isRecord(item)) {
      return responses;
    }

    const scenarioId = toSafeToken(item.scenarioId) as MockScenarioId;
    const parts = Array.isArray(item.parts) ? (item.parts as ChatPart[]) : [];
    if (!scenarioId || parts.length === 0) {
      return responses;
    }

    responses.push({
      scenarioId,
      parts,
    });

    return responses;
  }, []);
};

export const toAssistantAppendMessage = (response: MockScenarioResponse) => {
  return {
    role: "assistant" as const,
    content: toStaticResponseParts(response).map(mapChatPartToAssistantContent),
    metadata: {
      custom: {
        mockScenarioId: response.scenarioId,
      },
    },
    startRun: false,
  };
};

export const createMockChatModelAdapter = (
  options: CreateMockChatModelAdapterOptions
): ChatModelAdapter => {
  return {
    run: async (runOptions) => {
      const userInput = getLatestUserInput(runOptions.messages);
      const resolved = await resolveResponseForInput(
        userInput,
        options,
        runOptions.abortSignal
      );
      const primaryResult = resolved.primaryResponse;
      const allResults = [primaryResult, ...resolved.additionalResponses];
      const ignorePrescriptionCommands =
        options.shouldIgnorePrescriptionCommands?.() || false;
      for (const item of allResults) {
        if (ignorePrescriptionCommands || item.command !== "prescription") {
          continue;
        }

        const prescriptionMarkdownText = extractMarkdownTextFromResponse(
          item.response
        );
        options.onPrescriptionCommandResolved?.(prescriptionMarkdownText);
      }

      const visibleResults = allResults.filter(
        (item) => item.command !== "prescription"
      );

      if (visibleResults.length === 0) {
        return Promise.resolve({
          status: {
            type: "complete",
            reason: "unknown",
          },
        });
      }

      const visiblePrimaryResponse = visibleResults[0].response;
      const visibleAdditionalResponses = visibleResults
        .slice(1)
        .map((item) => item.response);
      const lastScenario =
        visibleResults[visibleResults.length - 1]?.response.scenarioId ||
        visiblePrimaryResponse.scenarioId;
      options.onScenarioResolved?.(lastScenario);

      if (visiblePrimaryResponse.error) {
        return Promise.resolve({
          status: {
            type: "incomplete",
            reason: "error",
            error: visiblePrimaryResponse.error,
          },
          metadata: {
            custom: {
              mockScenarioId: visiblePrimaryResponse.scenarioId,
            },
          },
        });
      }

      if (visiblePrimaryResponse.streamText) {
        return streamScenario(
          visiblePrimaryResponse,
          visibleAdditionalResponses,
          options.getStreamSpeed(),
          runOptions.abortSignal
        );
      }

      return Promise.resolve(
        toRunResult(visiblePrimaryResponse, visibleAdditionalResponses)
      );
    },
  };
};
